import React from "react";
import { ActionNode } from "../../../views/convoauto-pages/bot-studio/types";
import { TemplateModule } from "../../../views/marketing-pages/templates";
import { SendMessageCard } from "./action/send-message-card";
import { SetDelayTimeCard } from "./action/set-delay-time";
import { RunAtSpecificTimeActionCard } from "./action/runat-specific-time";
import { FetchCurrentDateCard } from "./action/current-date";
import { FetchAudienceCard } from "./action/audience";
import { SetCustomerAttributeCard } from "./action/customer-attribute";
import { BirthdayConditionCard } from "./action/birthday-condition";
import { ConditionCard } from "./action/condition";
import { SendBirthdayMessageCard } from "./action/send-birthday";
import { SendMessageTemplateActionCard } from "./action/message-template";
import { SendEmailCard } from "./action/send-email-card";
import { MarkAsSentCard } from "./action/mark-sent";
import { GenericActionCard } from "./action/generic-action";
import { WhatsAppMessageActionCard } from "./action/whatsapp-msg";
import { WaitTillCard } from "./action/wait-till";
import { SendMessageOut } from "./action/send-message-out";
import { StoreDataCard } from "./action/store-data";
import { CreateSupportTicketCard } from "./action/support-ticket";
import { SendInternalNotificationCard } from "./action/internal-notification";
import {
  ConfirmOrderCard,
  EscalateToHumanCard,
  RcsBranchCard,
  SendCarouselCard,
  SendMenuCard,
  SendRcsTemplateCard,
  SendTextCard,
  SetVariableCard,
  WaitForReplyCard,
} from "./action/rcs-actions";


type MediaUploadRequest = (data: FormData) => Promise<any[]>;

interface ActionNodeCardProps {
  node: ActionNode;
  onRemove: () => void;
  onUpdate?: (id: string, config: Record<string, any>) => void;
  onReset?: () => void;
  templates?: TemplateModule[];
  mediaUpload?: MediaUploadRequest;
  broadcastExcelUpload?: MediaUploadRequest;
  journeyId?: string | null;
  // Forwarded from parent so the Wait Until card can narrow the
  // date-attribute dropdown to fields the chosen event_type actually
  // exposes (via GET /v1/variables/dates?event_type=…).
  triggerEventType?: string;
  // When rendered inside the right-side config sidebar instead of on the
  // canvas, skip each card's own border/header chrome (the sidebar supplies
  // its own header) — see CardShell in ./action/card-handler.tsx.
  bare?: boolean;
}

export const ActionNodeCard: React.FC<ActionNodeCardProps> = ({
  node,
  onRemove,
  onUpdate = () => { },
  onReset = () => { },
  templates,
  mediaUpload,
  broadcastExcelUpload,
  journeyId,
  triggerEventType,
  bare,
}) => {
  const handleUpdate = (config: Record<string, any>) => onUpdate(node.id, config);

  // Send Message
  if (node.type === "Send Message") {
    return (
      <SendMessageCard
        node={node}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        mediaUpload={mediaUpload}
        bare={bare}
      />
    );
  }

  if (node.type === "Send Interactive Message") {
    return (
      <SendMessageOut
        node={node}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        templates={templates}
        bare={bare}
      />
    );
  }

  // Set Delay Time
  if (node.type === "Set Delay Time") {
    return (
      <SetDelayTimeCard
        config={node.config}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        bare={bare}
      />
    );
  }

  // Wait Till — pauses until backend signal arrives or timeout elapses
  if (node.type === "Wait Till" || node.type === "Wait Until") {
    return (
      <WaitTillCard
        config={node.type === "Wait Until" ? { ...node.config, waitMode: "date" } : node.config}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        eventType={triggerEventType}
        bare={bare}
      />
    );
  }

  // Run at Specific Time
  if (node.type === "Run at Specific Time") {
    return (
      <RunAtSpecificTimeActionCard
        config={node.config}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        bare={bare}
      />
    );
  }

  // Fetch Current Date
  if (node.type === "Fetch Current Date") {
    return (
      <FetchCurrentDateCard
        config={node.config}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        bare={bare}
      />
    );
  }

  // Fetch Audience
  if (node.type === "Fetch Audience") {
    return (
      <FetchAudienceCard
        config={node.config}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        templates={templates}
        broadcastExcelUpload={broadcastExcelUpload}
        journeyId={journeyId}
        bare={bare}
      />
    );
  }

  // Set Customer Attribute
  if (node.type === "Set Customer Attribute") {
    return (
      <SetCustomerAttributeCard
        config={node.config}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        bare={bare}
      />
    );
  }

  // Condition - Birthday variant
  if (node.type === "Condition" && node.config?.variant === "birthday") {
    return (
      <BirthdayConditionCard
        config={node.config}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        bare={bare}
      />
    );
  }

  // Condition - Regular
  if (node.type === "Condition") {
    return (
      <ConditionCard
        config={node.config}
        templates={templates}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        bare={bare}
      />
    );
  }

  // Send Birthday Message
  if (node.type === "Send Birthday Message") {
    return (
      <SendBirthdayMessageCard
        node={node}
        templates={templates}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        bare={bare}
      />
    );
  }

  // Send Message Template
  if (node.type === "Send Message Template") {
    return (
      <SendMessageTemplateActionCard
        node={node}
        templates={templates}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        bare={bare}
      />
    );
  }

  // Send Email
  if (node.type === "Send Email") {
    return (
      <SendEmailCard
        node={node}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        templates={templates}
        bare={bare}
      />
    );
  }

  // Mark as Sent
  if (node.type === "Mark as Sent") {
    return (
      <MarkAsSentCard
        config={node.config}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        bare={bare}
      />
    );
  }

  if (node.type === "WhatsApp Message") {
    return (
      < WhatsAppMessageActionCard
        node={node}
        templates={templates}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        bare={bare}
      />
    );
  }

  if (node.type === "Store Data") {
    return (
      <StoreDataCard
        node={node}
        onUpdate={handleUpdate}
        onDelete={onRemove}
        onReset={onReset}
        bare={bare}
      />
    );
  }

  if (node.type === "Create Support Ticket") {
    return <CreateSupportTicketCard node={node} onUpdate={handleUpdate} onDelete={onRemove} onReset={onReset} bare={bare} />;
  }

  if (node.type === "Send Internal Notification") {
    return <SendInternalNotificationCard node={node} templates={templates} onUpdate={handleUpdate} onDelete={onRemove} onReset={onReset} bare={bare} />;
  }

  // RCS-only action types
  if (node.type === "Send RCS Template") {
    return <SendRcsTemplateCard node={node} onUpdate={handleUpdate} onDelete={onRemove} onReset={onReset} bare={bare} />;
  }
  if (node.type === "Send Menu") {
    return <SendMenuCard node={node} onUpdate={handleUpdate} onDelete={onRemove} onReset={onReset} bare={bare} />;
  }
  if (node.type === "Send Carousel") {
    return <SendCarouselCard node={node} onUpdate={handleUpdate} onDelete={onRemove} onReset={onReset} bare={bare} />;
  }
  if (node.type === "Send Text") {
    return <SendTextCard node={node} onUpdate={handleUpdate} onDelete={onRemove} onReset={onReset} bare={bare} />;
  }
  if (node.type === "Wait for Reply") {
    return <WaitForReplyCard node={node} onUpdate={handleUpdate} onDelete={onRemove} onReset={onReset} bare={bare} />;
  }
  if (node.type === "Set Variable") {
    return <SetVariableCard node={node} onUpdate={handleUpdate} onDelete={onRemove} onReset={onReset} bare={bare} />;
  }
  if (node.type === "Escalate to Human") {
    return <EscalateToHumanCard node={node} onUpdate={handleUpdate} onDelete={onRemove} onReset={onReset} bare={bare} />;
  }
  if (node.type === "Confirm Order") {
    return <ConfirmOrderCard node={node} onUpdate={handleUpdate} onDelete={onRemove} onReset={onReset} bare={bare} />;
  }
  if (node.type === "Branch") {
    return <RcsBranchCard node={node} onUpdate={handleUpdate} onDelete={onRemove} onReset={onReset} bare={bare} />;
  }

  // Default fallback for other action types (Assign Agent, Add/Remove Tags, Call API, Close Conversation, Add to Broadcast)
  return (
    <GenericActionCard
      node={node}
      onUpdate={handleUpdate}
      onDelete={onRemove}
      onReset={onReset}
      bare={bare}
    />
  );
};